// Page Images Manager Component
import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { compressImage } from '../../utils/imageCompression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONFIG = [
  { page: 'home', section: 'about', label: 'Accueil - Section À Propos', defaultUrl: 'https://images.unsplash.com/photo-1530267981375-f0de937f5f13?w=800&q=80' },
  { page: 'equipment', section: 'quality', label: 'Équipement - Section Qualité', defaultUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80' },
  { page: 'services', section: 'main', label: 'Services - Image Principale', defaultUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80' },
];

// filterConfig: optional array to show only specific images
const PageImagesManager = ({ filterConfig = null }) => {
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const fileInputRefs = useRef({});

  const PAGE_IMAGES_CONFIG = filterConfig || DEFAULT_CONFIG;

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/page-images`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const imagesMap = {};
        data.forEach(img => { imagesMap[`${img.page}_${img.section}`] = img.image_url; });
        setImages(imagesMap);
      }
    } catch (err) {
      console.error('Error fetching page images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (page, section, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(prev => ({ ...prev, [`${page}_${section}`]: true }));
    try {
      // Compress image before upload
      const compressedFile = await compressImage(file, { maxWidth: 1200, quality: 0.85 });
      const formData = new FormData();
      formData.append('file', compressedFile);
      const uploadRes = await fetch(`${BACKEND_URL}/api/admin/upload`, { method: 'POST', credentials: 'include', body: formData });
      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        const imageUrl = `${BACKEND_URL}${uploadData.url}`;
        await fetch(`${BACKEND_URL}/api/admin/page-images`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ page, section, image_url: imageUrl })
        });
        setImages(prev => ({ ...prev, [`${page}_${section}`]: imageUrl }));
      }
    } catch (err) {
      console.error('Error uploading:', err);
    } finally {
      setSaving(prev => ({ ...prev, [`${page}_${section}`]: false }));
    }
  };

  const handleUrlChange = async (page, section, url) => {
    setImages(prev => ({ ...prev, [`${page}_${section}`]: url }));
  };

  const handleSaveUrl = async (page, section) => {
    const url = images[`${page}_${section}`];
    if (!url) return;
    setSaving(prev => ({ ...prev, [`${page}_${section}`]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/page-images`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ page, section, image_url: url })
      });
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSaving(prev => ({ ...prev, [`${page}_${section}`]: false }));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-light text-slate-800">Images des Pages</h2>
        <p className="text-sm text-slate-500 mt-1">Modifiez les images statiques affichées sur les différentes pages du site</p>
      </div>
      <div className="space-y-6">
        {PAGE_IMAGES_CONFIG.map(({ page, section, label, defaultUrl }) => {
          const key = `${page}_${section}`;
          const currentUrl = images[key] || defaultUrl;
          const isSaving = saving[key];
          return (
            <div key={key} className="border border-slate-200 rounded-xl p-4">
              <div className="flex gap-6">
                {/* Container d'image avec taille fixe et aspect ratio harmonieux */}
                <div className="w-56 h-40 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                  <img 
                    src={currentUrl} 
                    alt={label} 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-800 mb-3">{label}</h3>
                  <div className="flex gap-2 mb-3">
                    <input ref={el => fileInputRefs.current[key] = el} type="file" accept="image/*" onChange={(e) => handleFileUpload(page, section, e)} className="hidden" />
                    <Button variant="outline" size="sm" onClick={() => fileInputRefs.current[key]?.click()} disabled={isSaving}>
                      {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</> : <><Upload className="w-4 h-4 mr-2" /> Uploader une image</>}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={currentUrl} onChange={(e) => handleUrlChange(page, section, e.target.value)} placeholder="URL de l'image" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <Button size="sm" onClick={() => handleSaveUrl(page, section)} disabled={isSaving}><Save className="w-4 h-4 mr-1" /> Sauver</Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">💡 Astuce</h3>
        <p className="text-sm text-blue-800">Vous pouvez soit uploader une image directement, soit coller l'URL d'une image externe. Les changements seront visibles immédiatement sur le site.</p>
      </div>
    </div>
  );
};

export default PageImagesManager;
